import { useMemo } from 'react';
import { GameRunner } from './GameRunner';
import { countingRounds, pictureMatchRounds, stripDiacritics, toneRounds, type NumberItem, type ToneSet, type WordItem } from './logic';

type Finish = (score: number) => void;

export function PictureMatchGame({ items, onFinish }: { items: WordItem[]; onFinish: Finish }) {
  const rounds = useMemo(() => pictureMatchRounds(items, Math.min(3, items.length)), [items]);
  return (
    <GameRunner
      rounds={rounds}
      keyOf={(i) => i.word}
      renderPrompt={(r) => (
        <>
          <p className="muted">Which word matches the picture?</p>
          <p className="game-prompt" role="img" aria-label={r.answer.gloss}>
            {r.answer.picture ?? '❓'}
          </p>
        </>
      )}
      renderChoice={(i) => i.word}
      explain={(i) => `${i.word} means “${i.gloss}”.`}
      onFinish={onFinish}
    />
  );
}

export function ToneMarksGame({ sets, onFinish }: { sets: ToneSet[]; onFinish: Finish }) {
  const rounds = useMemo(() => toneRounds(sets), [sets]);
  return (
    <GameRunner
      rounds={rounds}
      keyOf={(i) => i.word}
      renderPrompt={(r) => (
        <>
          <p className="muted">
            Same letters — <span className="yo" lang="yo">“{stripDiacritics(r.answer.word)}”</span> — different tones. Which one means…
          </p>
          <p className="game-prompt" aria-hidden="true">
            {r.answer.picture ?? '🔤'}
          </p>
          <p style={{ fontSize: '1.4rem', fontWeight: 600 }}>“{r.answer.gloss}”</p>
        </>
      )}
      renderChoice={(i) => i.word}
      explain={(i) => `${i.word} = ${i.gloss}. The tone marks change the meaning!`}
      onFinish={onFinish}
    />
  );
}

export function CountingGame({ numbers, onFinish }: { numbers: NumberItem[]; onFinish: Finish }) {
  const rounds = useMemo(() => countingRounds(numbers, Math.min(4, numbers.length)), [numbers]);
  return (
    <GameRunner
      rounds={rounds}
      keyOf={(n) => String(n.value)}
      renderPrompt={(r) => (
        <>
          <p className="muted">Count the cowries. How many are there?</p>
          <div className="cowrie-row" role="img" aria-label={`${r.answer.value} cowrie shells`}>
            {Array.from({ length: r.answer.value }, (_, i) => (
              <span key={i}>🐚</span>
            ))}
          </div>
        </>
      )}
      renderChoice={(n) => n.word}
      explain={(n) => `${n.word} is ${n.value}.`}
      onFinish={onFinish}
    />
  );
}
